// src/pages/admin/StripeManagement.jsx
// Professional Stripe management panel for superadmins with 5 tabs:
// Overview, Plans, Webhook Logs, Coupons, Settings

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Skeleton,
  Collapse,
  InputAdornment,
  Snackbar,
  Checkbox,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import WebhookRoundedIcon from "@mui/icons-material/WebhookRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";

import { stripeAdminService } from "../../api/stripeAdminService";
import { API_BASE_URL } from "../../config";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function relativeTime(dateStr) {
  if (!dateStr) return "Never";
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

function formatPrice(amount, currency = "usd") {
  if (!amount && amount !== 0) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount / 100);
}

// ---------------------------------------------------------------------------
// Tab Panel
// ---------------------------------------------------------------------------
function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 3 }}>{children}</Box> : null;
}

// ---------------------------------------------------------------------------
// Stat Card
// ---------------------------------------------------------------------------
function StatCard({ icon, label, value, subtitle, color = "primary", loading }) {
  const theme = useTheme();
  const c = theme.palette[color] || theme.palette.primary;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        flex: 1,
        minWidth: 160,
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(c.main, 0.1),
          color: c.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={60} height={32} />
        ) : (
          <Typography
            variant="h5"
            fontWeight={700}
            lineHeight={1.2}
            sx={{ fontSize: { xs: "1rem", sm: "1.5rem" }, wordBreak: "break-word" }}
          >
            {value}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Config Row
// ---------------------------------------------------------------------------
function ConfigRow({ label, configured, preview, source }) {
  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 1, flexWrap: "wrap" }}>
      <FiberManualRecordRoundedIcon
        sx={{
          fontSize: 10,
          color: configured ? "success.main" : "text.disabled",
          mt: 0.6,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ width: { xs: 120, sm: 160 }, flexShrink: 0 }}
      >
        {label}
      </Typography>
      {configured ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          <CheckCircleRoundedIcon sx={{ fontSize: 16, color: "success.main", flexShrink: 0 }} />
          <Typography variant="body2" fontWeight={500} color="success.main" sx={{ flexShrink: 0 }}>
            Configured
          </Typography>
          {preview && (
            <Typography
              variant="body2"
              fontFamily="monospace"
              fontSize={12}
              color="text.secondary"
              sx={{
                bgcolor: "action.hover",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: { xs: 90, sm: 160 },
              }}
            >
              {preview}
            </Typography>
          )}
          {source && (
            <Chip label={source} size="small" variant="outlined" sx={{ height: 20, fontSize: 11, flexShrink: 0 }} />
          )}
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" spacing={1}>
          <ErrorRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
          <Typography variant="body2" color="text.disabled">
            Not configured
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------
function OverviewTab({ onNavigate }) {
  const theme = useTheme();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [configData, statsData] = await Promise.all([
        stripeAdminService.getConfig(),
        stripeAdminService.getStats(),
      ]);
      setConfig(configData);
      setStats(statsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await stripeAdminService.testConnection();
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  const isConnected = config?.effective_source !== "none";
  const mode = config?.detected_mode || "unknown";

  return (
    <Stack spacing={3}>
      {/* Connection Health Card */}
      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          border: isConnected && !loading
            ? `1px solid ${alpha(theme.palette.success.main, 0.3)}`
            : undefined,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: isConnected && !loading
              ? alpha(theme.palette.success.main, 0.04)
              : alpha(theme.palette.grey[500], 0.04),
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              {loading ? (
                <Skeleton variant="circular" width={12} height={12} />
              ) : (
                <FiberManualRecordRoundedIcon
                  sx={{
                    fontSize: 12,
                    color: isConnected ? "success.main" : "error.main",
                    animation: isConnected ? undefined : "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.4 },
                    },
                  }}
                />
              )}
              <Typography variant="subtitle1" fontWeight={700}>
                Stripe Connection
              </Typography>
              {!loading && mode !== "unknown" && (
                <Chip
                  label={mode === "test" ? "TEST MODE" : "LIVE MODE"}
                  size="small"
                  color={mode === "live" ? "error" : "warning"}
                  variant="outlined"
                  sx={{ fontWeight: 600, letterSpacing: 0.5, fontSize: 11 }}
                />
              )}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={handleTestConnection}
              disabled={testing || loading}
              startIcon={
                testing ? <CircularProgress size={14} /> : <LinkRoundedIcon />
              }
            >
              {testing ? "Testing..." : "Test Connection"}
            </Button>
          </Stack>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Test Result */}
          {testResult && (
            <Alert
              severity={testResult.success ? "success" : "error"}
              sx={{ mb: 2 }}
              onClose={() => setTestResult(null)}
            >
              {testResult.success ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Connected to {testResult.account_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Account: {testResult.account_id} &bull;{" "}
                    {testResult.livemode ? "Live" : "Test"} mode &bull;{" "}
                    {testResult.country?.toUpperCase()} &bull;{" "}
                    {testResult.default_currency?.toUpperCase()}
                  </Typography>
                </Stack>
              ) : (
                testResult.error
              )}
            </Alert>
          )}

          {/* Configuration Details */}
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            Configuration
          </Typography>

          {loading ? (
            <Stack spacing={1.5}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={28} />
              ))}
            </Stack>
          ) : (
            <>
              <ConfigRow
                label="Secret Key"
                configured={config?.has_secret_key || config?.env_has_secret_key}
                source={
                  config?.has_secret_key && config?.env_has_secret_key
                    ? "DB + ENV"
                    : config?.has_secret_key
                    ? "DB"
                    : config?.env_has_secret_key
                    ? "ENV"
                    : null
                }
              />
              <ConfigRow
                label="Publishable Key"
                configured={
                  !!config?.publishable_key || config?.env_has_publishable_key
                }
                preview={
                  config?.publishable_key
                    ? `${config.publishable_key.substring(0, 12)}...`
                    : config?.env_publishable_key_preview || null
                }
                source={
                  config?.publishable_key && config?.env_has_publishable_key
                    ? "DB + ENV"
                    : config?.publishable_key
                    ? "DB"
                    : config?.env_has_publishable_key
                    ? "ENV"
                    : null
                }
              />
              <ConfigRow
                label="Webhook Secret"
                configured={
                  config?.has_webhook_secret || config?.env_has_webhook_secret
                }
                source={
                  config?.has_webhook_secret && config?.env_has_webhook_secret
                    ? "DB + ENV"
                    : config?.has_webhook_secret
                    ? "DB"
                    : config?.env_has_webhook_secret
                    ? "ENV"
                    : null
                }
              />
              <ConfigRow
                label="Webhook URL"
                configured={
                  !!config?.webhook_url ||
                  (API_BASE_URL.includes("localhost") || API_BASE_URL.includes("127.0.0.1"))
                }
                preview={config?.webhook_url || null}
                source={
                  config?.webhook_url
                    ? "DB"
                    : (API_BASE_URL.includes("localhost") || API_BASE_URL.includes("127.0.0.1"))
                    ? "Stripe CLI"
                    : null
                }
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Quick Stats Row */}
      <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 2 }}>
        <StatCard
          icon={<InventoryRoundedIcon />}
          label="Active Plans"
          value={stats?.active_plans ?? 0}
          color="primary"
          loading={loading}
        />
        <StatCard
          icon={<WebhookRoundedIcon />}
          label="Webhooks (24h)"
          value={stats?.webhook_events_24h ?? 0}
          subtitle={stats ? `${stats.total_webhook_events} total` : undefined}
          color="secondary"
          loading={loading}
        />
        <StatCard
          icon={<PeopleRoundedIcon />}
          label="Active Subscriptions"
          value={stats?.active_subscriptions ?? 0}
          color="success"
          loading={loading}
        />
        <StatCard
          icon={<SyncRoundedIcon />}
          label="Last Sync"
          value={loading ? "" : relativeTime(stats?.last_sync)}
          color="info"
          loading={loading}
        />
      </Stack>

      {/* Quick Actions */}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
          Quick Actions
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SyncRoundedIcon />}
            onClick={() => onNavigate(1)}
          >
            Sync Products
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ReceiptLongRoundedIcon />}
            onClick={() => onNavigate(1)}
          >
            View Plans
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<WebhookRoundedIcon />}
            onClick={() => onNavigate(2)}
          >
            Webhook Logs
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LocalOfferRoundedIcon />}
            onClick={() => onNavigate(3)}
          >
            Coupons
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsRoundedIcon />}
            onClick={() => onNavigate(4)}
          >
            Settings
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Plans Tab
// ---------------------------------------------------------------------------
function PlansTab({ showSnackbar }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [syncResult, setSyncResult] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, plan: null });
  const [createDialog, setCreateDialog] = useState(false);
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [selected, setSelected] = useState([]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(plans.map((p) => p.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (planId) => {
    setSelected((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId]
    );
  };

  const handleBulkAction = async (action) => {
    if (selected.length === 0) return;
    const label = action === "activate" ? "activate" : "deactivate";
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selected.length} plan(s)?`)) return;
    try {
      await stripeAdminService.bulkPlanAction(selected, action);
      setSelected([]);
      await fetchPlans();
      showSnackbar?.(`${selected.length} plan(s) ${label}d successfully`);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await stripeAdminService.getPlans(true);
      setPlans(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError("");
    try {
      const result = await stripeAdminService.syncProducts();
      setSyncResult(result);
      await fetchPlans();
      showSnackbar?.(`Sync complete: ${result.products_synced || 0} products synced`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleDeactivate = async (planId) => {
    const plan = plans.find((p) => p.id === planId);
    const hasStripe = plan?.stripe_product_id;
    const msg = hasStripe
      ? "Deactivate this plan?\n\nThis will also archive the product in Stripe. Existing subscribers won't be affected, but no new subscriptions can use this plan."
      : "Deactivate this plan?";
    if (!window.confirm(msg)) return;
    try {
      await stripeAdminService.deactivatePlan(planId);
      await fetchPlans();
      showSnackbar?.(`Plan '${plan.display_name}' deactivated`);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleFeatures = (planId) => {
    setExpandedFeatures((prev) => ({ ...prev, [planId]: !prev[planId] }));
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={700}>
          Subscription Plans
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              syncing ? <CircularProgress size={14} /> : <SyncRoundedIcon />
            }
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? "Syncing..." : "Sync from Stripe"}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Add Plan
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {syncResult && (
        <Alert severity="success" onClose={() => setSyncResult(null)}>
          Sync complete: {syncResult.products_synced || 0} products synced,{" "}
          {syncResult.plans_created || 0} plans created,{" "}
          {syncResult.plans_updated || 0} plans updated.
        </Alert>
      )}

      {loading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={52} />
          ))}
        </Stack>
      ) : plans.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: "center" }}
        >
          <InventoryRoundedIcon
            sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
          />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            No plans configured
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, maxWidth: 400, mx: "auto" }}
          >
            Import your products from Stripe or create plans manually to get
            started with billing.
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
          >
            <Button
              variant="contained"
              startIcon={<SyncRoundedIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              Sync from Stripe
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => setCreateDialog(true)}
            >
              Create Manually
            </Button>
          </Stack>
        </Paper>
      ) : (
        <>
        {/* Bulk action toolbar */}
        {selected.length > 0 && (
          <Paper variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              {selected.length} selected
            </Typography>
            <Button size="small" variant="outlined" color="error" onClick={() => handleBulkAction("deactivate")}>
              Deactivate
            </Button>
            <Button size="small" variant="outlined" color="success" onClick={() => handleBulkAction("activate")}>
              Activate
            </Button>
            <Button size="small" onClick={() => setSelected([])}>
              Clear
            </Button>
          </Paper>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={selected.length > 0 && selected.length < plans.length}
                    checked={plans.length > 0 && selected.length === plans.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="center">Interval</TableCell>
                <TableCell align="center">Seats</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Features</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => (
                <React.Fragment key={plan.id}>
                  <TableRow
                    sx={{
                      opacity: plan.is_active ? 1 : 0.5,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.includes(plan.id)}
                        onChange={() => handleSelectOne(plan.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {plan.display_name}
                          </Typography>
                          {plan.is_popular && (
                            <Chip
                              icon={<StarRoundedIcon sx={{ fontSize: 14 }} />}
                              label="Popular"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          )}
                        </Stack>
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          color="text.secondary"
                        >
                          {plan.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700}>
                        {formatPrice(plan.price_amount, plan.price_currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`/ ${plan.billing_interval || "month"}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 12 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{plan.seat_limit}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={plan.is_active ? "Active" : "Inactive"}
                        color={plan.is_active ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {plan.features && plan.features.length > 0 ? (
                        <Chip
                          label={`${plan.features.length} features`}
                          size="small"
                          variant="outlined"
                          onClick={() => toggleFeatures(plan.id)}
                          onDelete={() => toggleFeatures(plan.id)}
                          deleteIcon={
                            expandedFeatures[plan.id] ? (
                              <ExpandLessRoundedIcon />
                            ) : (
                              <ExpandMoreRoundedIcon />
                            )
                          }
                          sx={{ cursor: "pointer" }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setEditDialog({ open: true, plan })
                          }
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {plan.is_active && (
                        <Tooltip title="Deactivate">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeactivate(plan.id)}
                          >
                            <DeleteRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Expandable features row */}
                  {expandedFeatures[plan.id] &&
                    plan.features &&
                    plan.features.length > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          sx={{ py: 1.5, bgcolor: "action.hover" }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ flexWrap: "wrap", gap: 0.5, pl: 2 }}
                          >
                            {plan.features.map((f, i) => (
                              <Chip
                                key={i}
                                label={f}
                                size="small"
                                variant="outlined"
                                icon={
                                  <CheckCircleRoundedIcon
                                    sx={{ fontSize: 14 }}
                                  />
                                }
                              />
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* Create Plan Dialog */}
      <PlanDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        onSave={async (planData) => {
          await stripeAdminService.createPlan(planData);
          setCreateDialog(false);
          await fetchPlans();
          showSnackbar?.("Plan created successfully");
        }}
      />

      {/* Edit Plan Dialog */}
      <PlanDialog
        open={editDialog.open}
        plan={editDialog.plan}
        onClose={() => setEditDialog({ open: false, plan: null })}
        onSave={async (planData) => {
          const result = await stripeAdminService.updatePlan(editDialog.plan.id, planData);
          setEditDialog({ open: false, plan: null });
          await fetchPlans();
          if (result.stripe_sync_warning) {
            setError(`Plan saved locally but Stripe sync had issues: ${result.stripe_sync_warning}`);
          } else {
            showSnackbar?.("Plan updated successfully");
          }
        }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Plan Create/Edit Dialog
// ---------------------------------------------------------------------------
function PlanDialog({ open, plan, onClose, onSave }) {
  const isEdit = Boolean(plan);
  const [form, setForm] = useState({
    name: "",
    display_name: "",
    description: "",
    stripe_price_id: "",
    price_amount: "",
    price_currency: "usd",
    billing_interval: "month",
    seat_limit: 1,
    features: "",
    is_active: true,
    sort_order: 0,
    is_popular: false,
    sync_to_stripe: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (plan) {
      setForm({
        name: plan.name || "",
        display_name: plan.display_name || "",
        description: plan.description || "",
        stripe_price_id: plan.stripe_price_id || "",
        price_amount: plan.price_amount ? String(plan.price_amount / 100) : "",
        price_currency: plan.price_currency || "usd",
        billing_interval: plan.billing_interval || "month",
        seat_limit: plan.seat_limit || 1,
        features: Array.isArray(plan.features) ? plan.features.join("\n") : "",
        is_active: plan.is_active !== false,
        sort_order: plan.sort_order || 0,
        is_popular: plan.is_popular || false,
        sync_to_stripe: true,
      });
    } else {
      setForm({
        name: "",
        display_name: "",
        description: "",
        stripe_price_id: "",
        price_amount: "",
        price_currency: "usd",
        billing_interval: "month",
        seat_limit: 1,
        features: "",
        is_active: true,
        sort_order: 0,
        is_popular: false,
        sync_to_stripe: true,
      });
    }
    setError("");
  }, [plan, open]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const data = {
        ...form,
        price_amount: form.price_amount
          ? Math.round(parseFloat(form.price_amount) * 100)
          : null,
        seat_limit: parseInt(form.seat_limit, 10) || 1,
        sort_order: parseInt(form.sort_order, 10) || 0,
        features: form.features
          ? form.features
              .split("\n")
              .map((f) => f.trim())
              .filter(Boolean)
          : [],
      };
      // Remove sync_to_stripe from update payloads (only used for create)
      if (isEdit) delete data.sync_to_stripe;
      await onSave(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const hasExistingStripeIds = isEdit && (plan?.stripe_product_id || plan?.stripe_price_id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Edit Plan" : "Create Plan"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Sync to Stripe toggle (create only) */}
          {!isEdit && (
            <Alert
              severity={form.sync_to_stripe ? "info" : "warning"}
              variant="outlined"
              icon={<SyncRoundedIcon />}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={form.sync_to_stripe}
                    onChange={(e) =>
                      setForm({ ...form, sync_to_stripe: e.target.checked })
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    {form.sync_to_stripe
                      ? "Will create Product + Price in Stripe automatically"
                      : "Local only - won't create in Stripe (you'll need to link IDs manually)"}
                  </Typography>
                }
              />
            </Alert>
          )}

          {/* Stripe sync indicator for edit */}
          {hasExistingStripeIds && (
            <Alert severity="info" variant="outlined" icon={<SyncRoundedIcon />}>
              <Typography variant="body2">
                Linked to Stripe. Changes to name, description, price, and features
                will sync automatically.
              </Typography>
            </Alert>
          )}

          <TextField
            label="Internal Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={isEdit}
            helperText="Lowercase identifier (e.g., starter, team, growth)"
            size="small"
            fullWidth
          />
          <TextField
            label="Display Name"
            value={form.display_name}
            onChange={(e) =>
              setForm({ ...form, display_name: e.target.value })
            }
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            size="small"
            fullWidth
          />
          {/* Only show Stripe Price ID field when NOT syncing (manual entry) */}
          {(!form.sync_to_stripe || isEdit) && (
            <TextField
              label="Stripe Price ID"
              value={form.stripe_price_id}
              onChange={(e) =>
                setForm({ ...form, stripe_price_id: e.target.value })
              }
              helperText={
                hasExistingStripeIds
                  ? `Product: ${plan.stripe_product_id || "none"}`
                  : "e.g., price_1234..."
              }
              size="small"
              fullWidth
              disabled={hasExistingStripeIds}
            />
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Price"
              value={form.price_amount}
              onChange={(e) =>
                setForm({ ...form, price_amount: e.target.value })
              }
              helperText="In dollars (e.g., 29.00)"
              size="small"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Currency"
              value={form.price_currency}
              onChange={(e) =>
                setForm({ ...form, price_currency: e.target.value })
              }
              size="small"
              sx={{ width: 100 }}
            />
            <TextField
              label="Interval"
              value={form.billing_interval}
              onChange={(e) =>
                setForm({ ...form, billing_interval: e.target.value })
              }
              size="small"
              sx={{ width: 120 }}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Seat Limit"
              value={form.seat_limit}
              onChange={(e) =>
                setForm({ ...form, seat_limit: e.target.value })
              }
              size="small"
              type="number"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Sort Order"
              value={form.sort_order}
              onChange={(e) =>
                setForm({ ...form, sort_order: e.target.value })
              }
              size="small"
              type="number"
              sx={{ flex: 1 }}
            />
          </Stack>
          <TextField
            label="Features (one per line)"
            value={form.features}
            onChange={(e) =>
              setForm({ ...form, features: e.target.value })
            }
            multiline
            rows={4}
            size="small"
            fullWidth
            helperText="Enter one feature per line"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
            }
            label="Active"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_popular}
                onChange={(e) =>
                  setForm({ ...form, is_popular: e.target.checked })
                }
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <StarRoundedIcon sx={{ fontSize: 16, color: form.is_popular ? "warning.main" : "text.disabled" }} />
                <span>Mark as Popular</span>
              </Stack>
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? (
            <CircularProgress size={20} />
          ) : isEdit ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Webhook Logs Tab
// ---------------------------------------------------------------------------
function WebhookLogsTab({ showSnackbar }) {
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [endpoints, setEndpoints] = useState([]);
  const [endpointsLoading, setEndpointsLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isLocal = API_BASE_URL.includes("localhost") || API_BASE_URL.includes("127.0.0.1");
  const cliCommand = `stripe listen --forward-to ${API_BASE_URL.replace(/\/$/, "")}/api/billing/webhook --events checkout.session.completed,customer.subscription.created,customer.subscription.updated,customer.subscription.deleted,invoice.payment_succeeded,invoice.payment_failed`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar?.("Copied to clipboard");
  };

  const fetchEndpoints = useCallback(async () => {
    setEndpointsLoading(true);
    try {
      const data = await stripeAdminService.getWebhookEndpoints();
      setEndpoints(data);
    } catch {
      // silently fail - endpoints are supplementary info
    } finally {
      setEndpointsLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const filters = { limit: 50 };
      if (typeFilter !== "all") filters.event_type = typeFilter;
      if (statusFilter !== "all") filters.event_status = statusFilter;
      const data = await stripeAdminService.getWebhookEvents(filters);
      setEvents(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    fetchEndpoints();
    fetchEvents();
  }, [fetchEndpoints, fetchEvents]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchEvents, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchEvents]);

  const handleRegisterWebhook = async () => {
    setRegistering(true);
    try {
      await stripeAdminService.registerWebhookEndpoint({});
      showSnackbar?.("Webhook registered and signing secret saved to database");
      fetchEndpoints();
    } catch (err) {
      showSnackbar?.(err.message, "error");
    } finally {
      setRegistering(false);
    }
  };

  const handleDeleteWebhook = async (id) => {
    setDeleteConfirm(null);
    try {
      await stripeAdminService.deleteWebhookEndpoint(id);
      showSnackbar?.("Webhook endpoint deleted and signing secret cleared");
      fetchEndpoints();
    } catch (err) {
      showSnackbar?.(err.message, "error");
    }
  };

  const statusColor = (s) => {
    if (s === "processed") return "success";
    if (s === "failed") return "error";
    return "default";
  };

  const eventTypeCategories = [
    { label: "All", value: "all" },
    { label: "Checkout", value: "checkout.session.completed" },
    { label: "Subscription", value: "customer.subscription" },
    { label: "Invoice", value: "invoice" },
  ];

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h6" fontWeight={700}>
          Webhook Events
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Auto-refresh
              </Typography>
            }
            sx={{ mr: 1 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={fetchEvents}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Webhook Endpoint Status */}
      {endpointsLoading ? (
        <Skeleton variant="rounded" height={72} />
      ) : endpoints.length > 0 ? (
        endpoints.map((ep) => (
          <Paper
            key={ep.id}
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.success.main, 0.04),
              borderColor: alpha(theme.palette.success.main, 0.3),
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CheckCircleRoundedIcon color="success" sx={{ fontSize: 20 }} />
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" fontWeight={600}>
                      Webhook Endpoint
                    </Typography>
                    <Chip
                      label={ep.status === "enabled" ? "Active" : ep.status}
                      color={ep.status === "enabled" ? "success" : "default"}
                      size="small"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                    {ep.livemode && (
                      <Chip label="Live" color="error" size="small" variant="outlined" sx={{ fontSize: 11, height: 20 }} />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                    {ep.url}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    {ep.enabled_events?.length || 0} events
                  </Typography>
                </Box>
              </Stack>
              <Tooltip title="Delete endpoint and clear signing secret">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteConfirm(ep.id)}
                >
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Paper>
        ))
      ) : isLocal ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            bgcolor: alpha(theme.palette.info.main, 0.04),
            borderColor: alpha(theme.palette.info.main, 0.3),
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TerminalRoundedIcon color="info" sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Local Development — Use Stripe CLI
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stripe cannot send webhooks to localhost. Use the Stripe CLI to forward events to your local server.
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={1.5} sx={{ pl: 0.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                1. Install &amp; login
              </Typography>
              <Box
                sx={{
                  bgcolor: "grey.900",
                  color: "grey.100",
                  p: 1.5,
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: 12,
                  position: "relative",
                  overflow: "auto",
                }}
              >
                <Box component="span" sx={{ color: "grey.500" }}>$</Box> brew install stripe/stripe-cli/stripe{"\n"}
                <Box component="span" sx={{ color: "grey.500" }}>$</Box> stripe login
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard("brew install stripe/stripe-cli/stripe && stripe login")}
                  sx={{ position: "absolute", top: 4, right: 4, color: "grey.400" }}
                >
                  <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>

              <Typography variant="caption" fontWeight={600} color="text.secondary">
                2. Forward events to your local backend
              </Typography>
              <Box
                sx={{
                  bgcolor: "grey.900",
                  color: "grey.100",
                  p: 1.5,
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: 12,
                  position: "relative",
                  overflow: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                <Box component="span" sx={{ color: "grey.500" }}>$</Box> {cliCommand}
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(cliCommand)}
                  sx={{ position: "absolute", top: 4, right: 4, color: "grey.400" }}
                >
                  <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>

              <Typography variant="caption" fontWeight={600} color="text.secondary">
                3. Copy the signing secret
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The CLI will output: <strong>Ready! Your webhook signing secret is whsec_...</strong>{" "}
                Paste it in the <strong>Settings</strong> tab under "Webhook Secret" or set it as{" "}
                <code>STRIPE_WEBHOOK_SECRET</code> in your <code>.env</code>.
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.04),
            borderColor: alpha(theme.palette.warning.main, 0.3),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <InfoOutlinedIcon color="warning" sx={{ fontSize: 20 }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  No webhook endpoint configured
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Register a webhook endpoint to receive Stripe events automatically. The signing secret will be saved to the database.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={registering ? <CircularProgress size={16} color="inherit" /> : <LinkRoundedIcon />}
              onClick={handleRegisterWebhook}
              disabled={registering}
              sx={{ whiteSpace: "nowrap" }}
            >
              {registering ? "Registering..." : "Configure Webhook"}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Webhook Endpoint?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will remove the webhook endpoint from Stripe and clear the signing secret from the database. You will stop receiving webhook events until a new endpoint is registered.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDeleteWebhook(deleteConfirm)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filters */}
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: "center", mr: 1 }}
        >
          Type:
        </Typography>
        {eventTypeCategories.map((cat) => (
          <Chip
            key={cat.value}
            label={cat.label}
            size="small"
            variant={typeFilter === cat.value ? "filled" : "outlined"}
            color={typeFilter === cat.value ? "primary" : "default"}
            onClick={() => setTypeFilter(cat.value)}
            sx={{ cursor: "pointer" }}
          />
        ))}
        <Box sx={{ width: 16 }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ alignSelf: "center", mr: 1 }}
        >
          Status:
        </Typography>
        {["all", "processed", "failed"].map((s) => (
          <Chip
            key={s}
            label={s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            size="small"
            variant={statusFilter === s ? "filled" : "outlined"}
            color={
              statusFilter === s
                ? s === "failed"
                  ? "error"
                  : s === "processed"
                  ? "success"
                  : "primary"
                : "default"
            }
            onClick={() => setStatusFilter(s)}
            sx={{ cursor: "pointer" }}
          />
        ))}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Stack spacing={1}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : events.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: "center" }}
        >
          <WebhookRoundedIcon
            sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
          />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            No webhook events yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 480, mx: "auto", mb: 2 }}
          >
            Webhook events will appear here when Stripe sends notifications for
            completed checkouts, subscription changes, invoice payments, and
            other billing events.
          </Typography>
          {endpoints.length === 0 && !isLocal && (
            <Button
              variant="contained"
              size="small"
              startIcon={registering ? <CircularProgress size={16} color="inherit" /> : <LinkRoundedIcon />}
              onClick={handleRegisterWebhook}
              disabled={registering}
              sx={{ mt: 1 }}
            >
              {registering ? "Registering..." : "Configure Webhook"}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Event ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Received</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((evt) => (
                <React.Fragment key={evt.id}>
                  <TableRow
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      setExpandedRow(expandedRow === evt.id ? null : evt.id)
                    }
                  >
                    <TableCell sx={{ width: 40, pr: 0 }}>
                      {expandedRow === evt.id ? (
                        <ExpandLessRoundedIcon
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      ) : (
                        <ExpandMoreRoundedIcon
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize={12}
                      >
                        {evt.stripe_event_id
                          ? `${evt.stripe_event_id.substring(0, 24)}...`
                          : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={evt.event_type}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={evt.status}
                        color={statusColor(evt.status)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          evt.created_at
                            ? new Date(evt.created_at).toLocaleString()
                            : ""
                        }
                      >
                        <Typography variant="body2" fontSize={12}>
                          {relativeTime(evt.created_at)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontSize={12}
                        color="error"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {evt.error_message || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {expandedRow === evt.id && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 2, bgcolor: "action.hover" }}
                      >
                        <Stack spacing={1} sx={{ pl: 5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Full Event ID
                          </Typography>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            fontSize={12}
                          >
                            {evt.stripe_event_id}
                          </Typography>
                          {evt.processed_at && (
                            <>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Processed At
                              </Typography>
                              <Typography variant="body2" fontSize={12}>
                                {new Date(evt.processed_at).toLocaleString()}
                              </Typography>
                            </>
                          )}
                          {evt.error_message && (
                            <>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Error Details
                              </Typography>
                              <Typography
                                variant="body2"
                                fontSize={12}
                                color="error"
                              >
                                {evt.error_message}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Coupons Tab
// ---------------------------------------------------------------------------
function CouponsTab({ showSnackbar }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createDialog, setCreateDialog] = useState(false);
  const [expandedCoupon, setExpandedCoupon] = useState(null);
  const [promoCodes, setPromoCodes] = useState({});
  const [promoLoading, setPromoLoading] = useState({});

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await stripeAdminService.getCoupons();
      setCoupons(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleExpand = async (couponId) => {
    if (expandedCoupon === couponId) {
      setExpandedCoupon(null);
      return;
    }
    setExpandedCoupon(couponId);
    if (!promoCodes[couponId]) {
      setPromoLoading((prev) => ({ ...prev, [couponId]: true }));
      try {
        const codes = await stripeAdminService.getPromoCodes(couponId);
        setPromoCodes((prev) => ({ ...prev, [couponId]: codes }));
      } catch {
        setPromoCodes((prev) => ({ ...prev, [couponId]: [] }));
      } finally {
        setPromoLoading((prev) => ({ ...prev, [couponId]: false }));
      }
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm("Delete this coupon? This cannot be undone.")) return;
    try {
      await stripeAdminService.deleteCoupon(couponId);
      await fetchCoupons();
      showSnackbar?.("Coupon deleted successfully");
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.percent_off) return `${coupon.percent_off}% off`;
    if (coupon.amount_off) return `${formatPrice(coupon.amount_off, coupon.currency || "usd")} off`;
    return "N/A";
  };

  const formatDuration = (coupon) => {
    if (coupon.duration === "once") return "Once";
    if (coupon.duration === "forever") return "Forever";
    if (coupon.duration === "repeating") return `${coupon.duration_in_months} months`;
    return coupon.duration;
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={700}>
          Coupons & Promotion Codes
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={fetchCoupons}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Create Coupon
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={52} />
          ))}
        </Stack>
      ) : coupons.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: "center" }}>
          <LocalOfferRoundedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            No coupons yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 440, mx: "auto" }}>
            Create coupons with optional promotion codes. Customers can enter promotion codes
            during checkout to apply discounts.
          </Typography>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setCreateDialog(true)}>
            Create First Coupon
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Name / ID</TableCell>
                <TableCell align="center">Discount</TableCell>
                <TableCell align="center">Duration</TableCell>
                <TableCell align="center">Usage</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map((coupon) => (
                <React.Fragment key={coupon.id}>
                  <TableRow
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleExpand(coupon.id)}
                  >
                    <TableCell sx={{ width: 40, pr: 0 }}>
                      {expandedCoupon === coupon.id ? (
                        <ExpandLessRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                      ) : (
                        <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight={600}>
                          {coupon.name || coupon.id}
                        </Typography>
                        {coupon.name && (
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                            {coupon.id}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={formatDiscount(coupon)} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{formatDuration(coupon)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {coupon.times_redeemed}
                        {coupon.max_redemptions ? ` / ${coupon.max_redemptions}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={coupon.valid ? "Valid" : "Expired"}
                        color={coupon.valid ? "success" : "default"}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(coupon.id);
                          }}
                        >
                          <DeleteRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  {/* Expandable promo codes row */}
                  {expandedCoupon === coupon.id && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 2, bgcolor: "action.hover" }}>
                        <Stack spacing={1} sx={{ pl: 5 }}>
                          <Typography variant="overline" color="text.secondary">
                            Promotion Codes
                          </Typography>
                          {promoLoading[coupon.id] ? (
                            <Skeleton height={32} width={200} />
                          ) : promoCodes[coupon.id]?.length > 0 ? (
                            <Stack spacing={0.5}>
                              {promoCodes[coupon.id].map((pc) => (
                                <Stack key={pc.id} direction="row" alignItems="center" spacing={1.5}>
                                  <Chip
                                    label={pc.code}
                                    size="small"
                                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                                  />
                                  <Chip
                                    label={pc.active ? "Active" : "Inactive"}
                                    size="small"
                                    color={pc.active ? "success" : "default"}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Used: {pc.times_redeemed}
                                    {pc.max_redemptions ? ` / ${pc.max_redemptions}` : ""}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No promotion codes for this coupon.
                            </Typography>
                          )}
                          {coupon.redeem_by && (
                            <>
                              <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>
                                Expires
                              </Typography>
                              <Typography variant="body2">
                                {new Date(coupon.redeem_by * 1000).toLocaleDateString()}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CouponDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        onSave={async (data) => {
          await stripeAdminService.createCoupon(data);
          setCreateDialog(false);
          await fetchCoupons();
          showSnackbar?.("Coupon created successfully");
        }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Coupon Create Dialog
// ---------------------------------------------------------------------------
function CouponDialog({ open, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    discountType: "percent",
    percent_off: "",
    amount_off: "",
    currency: "usd",
    duration: "once",
    duration_in_months: "",
    max_redemptions: "",
    redeem_by: "",
    promo_code: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        name: "",
        discountType: "percent",
        percent_off: "",
        amount_off: "",
        currency: "usd",
        duration: "once",
        duration_in_months: "",
        max_redemptions: "",
        redeem_by: "",
        promo_code: "",
      });
      setError("");
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const data = { name: form.name, duration: form.duration };
      if (form.discountType === "percent") {
        data.percent_off = parseFloat(form.percent_off);
      } else {
        data.amount_off = Math.round(parseFloat(form.amount_off) * 100);
        data.currency = form.currency;
      }
      if (form.duration === "repeating" && form.duration_in_months) {
        data.duration_in_months = parseInt(form.duration_in_months, 10);
      }
      if (form.max_redemptions) {
        data.max_redemptions = parseInt(form.max_redemptions, 10);
      }
      if (form.redeem_by) {
        data.redeem_by = Math.floor(new Date(form.redeem_by).getTime() / 1000);
      }
      if (form.promo_code.trim()) {
        data.promo_code = form.promo_code.trim().toUpperCase();
      }
      await onSave(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Coupon</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Coupon Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            size="small"
            fullWidth
            helperText="Displayed in Stripe Dashboard (e.g., Launch Discount)"
          />

          {/* Discount type selector */}
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Discount Type
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                label="Percentage"
                variant={form.discountType === "percent" ? "filled" : "outlined"}
                color={form.discountType === "percent" ? "primary" : "default"}
                onClick={() => setForm({ ...form, discountType: "percent" })}
                sx={{ cursor: "pointer" }}
              />
              <Chip
                label="Fixed Amount"
                variant={form.discountType === "amount" ? "filled" : "outlined"}
                color={form.discountType === "amount" ? "primary" : "default"}
                onClick={() => setForm({ ...form, discountType: "amount" })}
                sx={{ cursor: "pointer" }}
              />
            </Stack>
          </Stack>

          {form.discountType === "percent" ? (
            <TextField
              label="Percent Off"
              value={form.percent_off}
              onChange={(e) => setForm({ ...form, percent_off: e.target.value })}
              size="small"
              type="number"
              inputProps={{ min: 1, max: 100, step: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              fullWidth
            />
          ) : (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Amount Off"
                value={form.amount_off}
                onChange={(e) => setForm({ ...form, amount_off: e.target.value })}
                size="small"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{ flex: 1 }}
                helperText="In dollars"
              />
              <TextField
                label="Currency"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                size="small"
                sx={{ width: 100 }}
              />
            </Stack>
          )}

          {/* Duration selector */}
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Duration
            </Typography>
            <Stack direction="row" spacing={1}>
              {[
                { label: "Once", value: "once" },
                { label: "Repeating", value: "repeating" },
                { label: "Forever", value: "forever" },
              ].map((d) => (
                <Chip
                  key={d.value}
                  label={d.label}
                  variant={form.duration === d.value ? "filled" : "outlined"}
                  color={form.duration === d.value ? "primary" : "default"}
                  onClick={() => setForm({ ...form, duration: d.value })}
                  sx={{ cursor: "pointer" }}
                />
              ))}
            </Stack>
          </Stack>

          {form.duration === "repeating" && (
            <TextField
              label="Duration in Months"
              value={form.duration_in_months}
              onChange={(e) => setForm({ ...form, duration_in_months: e.target.value })}
              size="small"
              type="number"
              inputProps={{ min: 1 }}
              fullWidth
            />
          )}

          <TextField
            label="Promotion Code (optional)"
            value={form.promo_code}
            onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
            size="small"
            fullWidth
            helperText="Customers enter this at checkout (e.g., LAUNCH20)"
            InputProps={{ sx: { fontFamily: "monospace", textTransform: "uppercase" } }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Max Redemptions (optional)"
              value={form.max_redemptions}
              onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
              size="small"
              type="number"
              inputProps={{ min: 1 }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Expiry Date (optional)"
              value={form.redeem_by}
              onChange={(e) => setForm({ ...form, redeem_by: e.target.value })}
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.name || (!form.percent_off && !form.amount_off)}
        >
          {saving ? <CircularProgress size={20} /> : "Create Coupon"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Settings Tab
// ---------------------------------------------------------------------------
function SettingsTab() {
  const theme = useTheme();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");
  const [howToOpen, setHowToOpen] = useState(false);
  const [testPaymentOpen, setTestPaymentOpen] = useState(false);

  // API Keys form
  const [keysForm, setKeysForm] = useState({
    environment: "test",
    publishable_key: "",
    secret_key: "",
    webhook_secret: "",
    webhook_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    stripeAdminService
      .getConfig()
      .then((data) => {
        setConfig(data);
        // Pre-fill form with existing values where available
        setKeysForm((prev) => ({
          ...prev,
          environment: data.detected_mode !== "unknown" ? data.detected_mode : "test",
          publishable_key: data.publishable_key || "",
          webhook_url: data.webhook_url || "",
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 2000);
    });
  };

  const handleSaveKeys = async () => {
    const hasExistingSecret = config?.has_secret_key || config?.env_has_secret_key;
    if (!keysForm.publishable_key) {
      setSaveError("Publishable Key is required.");
      return;
    }
    if (!keysForm.secret_key && !hasExistingSecret) {
      setSaveError("Secret Key is required for first-time setup.");
      return;
    }
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);
    try {
      // Only send secret fields if user typed something
      const payload = {
        environment: keysForm.environment,
        publishable_key: keysForm.publishable_key,
        webhook_url: keysForm.webhook_url || undefined,
      };
      if (keysForm.secret_key) payload.secret_key = keysForm.secret_key;
      if (keysForm.webhook_secret) payload.webhook_secret = keysForm.webhook_secret;

      const updated = await stripeAdminService.updateConfig(payload);
      setConfig(updated);
      setSaveSuccess(true);
      // Clear secret fields after save (they're stored encrypted)
      setKeysForm((prev) => ({ ...prev, secret_key: "", webhook_secret: "" }));
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Auto-detect webhook URL from backend base
  const detectedWebhookUrl = config?.webhook_url || `${API_BASE_URL}/api/billing/webhook`;

  return (
    <Stack spacing={3}>
      <Typography variant="h6" fontWeight={700}>
        Settings
      </Typography>

      {/* API Keys Configuration */}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PaymentRoundedIcon color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              API Keys Configuration
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure your Stripe API keys here. Keys saved here are stored
            encrypted in the database and take priority over environment
            variables.
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError("")}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              API keys saved successfully. The connection is now using DB configuration.
            </Alert>
          )}

          {/* Current source indicator */}
          {!loading && config?.effective_source && (
            <Alert
              severity={config.effective_source === "none" ? "warning" : "info"}
              variant="outlined"
              sx={{ mb: 2 }}
            >
              {config.effective_source === "env" && (
                <>Currently using keys from <strong>environment variables</strong>. Saving keys here will override them.</>
              )}
              {config.effective_source === "db" && (
                <>Currently using keys from <strong>database</strong> (saved via this form).</>
              )}
              {config.effective_source === "none" && (
                <>No Stripe keys configured. Enter your keys below to get started.</>
              )}
            </Alert>
          )}

          <Stack spacing={2.5}>
            <Stack direction="row" spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={keysForm.environment === "live"}
                    onChange={(e) =>
                      setKeysForm({
                        ...keysForm,
                        environment: e.target.checked ? "live" : "test",
                      })
                    }
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">
                      {keysForm.environment === "live" ? "Live Mode" : "Test Mode"}
                    </Typography>
                    <Chip
                      label={keysForm.environment === "live" ? "LIVE" : "SANDBOX"}
                      size="small"
                      color={keysForm.environment === "live" ? "error" : "warning"}
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  </Stack>
                }
              />
            </Stack>

            <TextField
              label="Publishable Key"
              value={keysForm.publishable_key}
              onChange={(e) =>
                setKeysForm({ ...keysForm, publishable_key: e.target.value })
              }
              size="small"
              fullWidth
              placeholder={keysForm.environment === "test" ? "pk_test_..." : "pk_live_..."}
              helperText={
                config?.env_has_publishable_key
                  ? `ENV also configured: ${config.env_publishable_key_preview || "yes"}`
                  : "Starts with pk_test_ (sandbox) or pk_live_ (production)"
              }
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
            />

            <TextField
              label="Secret Key"
              value={keysForm.secret_key}
              onChange={(e) =>
                setKeysForm({ ...keysForm, secret_key: e.target.value })
              }
              size="small"
              fullWidth
              type="password"
              placeholder={keysForm.environment === "test" ? "sk_test_..." : "sk_live_..."}
              helperText={
                config?.has_secret_key || config?.env_has_secret_key
                  ? `Already configured via ${config?.has_secret_key ? "DB" : "ENV"}. Leave blank to keep current value, or enter new key to replace.`
                  : "Starts with sk_test_ (sandbox) or sk_live_ (production). Found in Stripe Dashboard > API Keys."
              }
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
            />

            <TextField
              label="Webhook Signing Secret"
              value={keysForm.webhook_secret}
              onChange={(e) =>
                setKeysForm({ ...keysForm, webhook_secret: e.target.value })
              }
              size="small"
              fullWidth
              type="password"
              placeholder="whsec_..."
              helperText={
                config?.has_webhook_secret || config?.env_has_webhook_secret
                  ? `Already configured via ${config?.has_webhook_secret ? "DB" : "ENV"}. Leave blank to keep, or enter new to replace.`
                  : "Found in Stripe Dashboard > Developers > Webhooks > your endpoint > Signing secret"
              }
              InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
            />

            <Button
              variant="contained"
              onClick={handleSaveKeys}
              disabled={saving || (!keysForm.publishable_key && !keysForm.secret_key)}
              sx={{ alignSelf: "flex-start" }}
            >
              {saving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {saving ? "Saving..." : "Save API Keys"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Webhook URL */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WebhookRoundedIcon color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Your Webhook Endpoint URL
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            This is the URL you need to paste in the Stripe Dashboard under
            Developers &rarr; Webhooks &rarr; "Add endpoint". It points to your
            backend's billing webhook handler.
          </Typography>
          {loading ? (
            <Skeleton height={40} />
          ) : (
            <TextField
              value={detectedWebhookUrl}
              size="small"
              fullWidth
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", fontSize: 13 },
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied === "webhook" ? "Copied!" : "Copy to clipboard"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(detectedWebhookUrl, "webhook")}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              Copy this URL and paste it in{" "}
              <strong>Stripe Dashboard &rarr; Developers &rarr; Webhooks &rarr; Add endpoint</strong>.
              After adding, Stripe will give you a <strong>Signing secret</strong> (starts with <code>whsec_</code>) -
              paste that in the "Webhook Signing Secret" field above.
            </Typography>
          </Alert>
        </Stack>
      </Paper>

      {/* How to Configure - Step by Step */}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "&:hover": { bgcolor: "action.hover" },
          }}
          onClick={() => setHowToOpen(!howToOpen)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoOutlinedIcon color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Step-by-step setup guide
            </Typography>
          </Stack>
          {howToOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
        </Box>
        <Collapse in={howToOpen}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                1. Get your API keys
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                Go to <strong>Stripe Dashboard &rarr; Developers &rarr; API Keys</strong>.
                Copy the <strong>Publishable key</strong> (pk_test_...) and <strong>Secret key</strong> (sk_test_...).
                Paste them in the form above.
              </Typography>

              <Typography variant="subtitle2" fontWeight={600} color="primary">
                2. Set up the webhook
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                Go to <strong>Stripe Dashboard &rarr; Developers &rarr; Webhooks</strong>.
                Click <strong>"Add endpoint"</strong> and paste this URL:
              </Typography>
              <Box
                sx={{
                  pl: 2,
                  py: 1,
                  px: 2,
                  ml: 2,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" fontFamily="monospace" fontSize={13}>
                  {detectedWebhookUrl}
                </Typography>
                <IconButton size="small" onClick={() => handleCopy(detectedWebhookUrl, "webhook2")}>
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </Box>

              <Typography variant="subtitle2" fontWeight={600} color="primary">
                3. Select webhook events
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                Select these events to listen to:
              </Typography>
              <Box sx={{ pl: 4 }}>
                {[
                  "checkout.session.completed",
                  "customer.subscription.created",
                  "customer.subscription.updated",
                  "customer.subscription.deleted",
                  "invoice.payment_succeeded",
                  "invoice.payment_failed",
                ].map((evt) => (
                  <Typography
                    key={evt}
                    variant="body2"
                    fontFamily="monospace"
                    fontSize={12}
                    color="text.secondary"
                    sx={{ py: 0.25 }}
                  >
                    &bull; {evt}
                  </Typography>
                ))}
              </Box>

              <Typography variant="subtitle2" fontWeight={600} color="primary">
                4. Save the Signing secret
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                After adding the endpoint, Stripe will show a <strong>Signing secret</strong> (starts with <code>whsec_</code>).
                Copy it and paste it in the "Webhook Signing Secret" field above, then click "Save API Keys".
              </Typography>

              <Typography variant="subtitle2" fontWeight={600} color="primary">
                5. Sync your products
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                Go to the <strong>Plans</strong> tab and click <strong>"Sync from Stripe"</strong> to import
                your products and prices. If you don't have products yet, create them in
                the Stripe Dashboard first.
              </Typography>
            </Stack>
          </Box>
        </Collapse>
      </Paper>

      {/* Test Sandbox Payments */}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "&:hover": { bgcolor: "action.hover" },
          }}
          onClick={() => setTestPaymentOpen(!testPaymentOpen)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PaymentRoundedIcon color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              How to test sandbox payments
            </Typography>
            <Chip label="SANDBOX" size="small" color="warning" variant="outlined" sx={{ fontSize: 11 }} />
          </Stack>
          {testPaymentOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
        </Box>
        <Collapse in={testPaymentOpen}>
          <Box sx={{ px: 3, pb: 3 }}>
            <Stack spacing={2}>
              <Alert severity="info" variant="outlined">
                Stripe test mode lets you simulate payments without real money.
                Make sure you're using <strong>test keys</strong> (pk_test_ / sk_test_).
              </Alert>

              <Typography variant="subtitle2" fontWeight={600}>
                Test card numbers
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Card</TableCell>
                      <TableCell>Number</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      { name: "Visa (success)", number: "4242 4242 4242 4242", result: "Payment succeeds" },
                      { name: "Visa (decline)", number: "4000 0000 0000 0002", result: "Payment declined" },
                      { name: "Auth required", number: "4000 0025 0000 3155", result: "3D Secure required" },
                      { name: "Insufficient funds", number: "4000 0000 0000 9995", result: "Insufficient funds" },
                    ].map((card) => (
                      <TableRow key={card.number}>
                        <TableCell>
                          <Typography variant="body2" fontSize={12}>{card.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2" fontFamily="monospace" fontSize={12}>
                              {card.number}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(card.number.replace(/\s/g, ""), card.number);
                              }}
                            >
                              <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={12} color="text.secondary">
                            {card.result}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" color="text.secondary">
                Use any future expiry date (e.g., <strong>12/30</strong>), any 3-digit CVC (e.g., <strong>123</strong>),
                and any billing ZIP code.
              </Typography>

              <Typography variant="subtitle2" fontWeight={600}>
                To verify payments are working:
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                  1. Click <strong>"Test Connection"</strong> in the Overview tab to confirm your API key works
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                  2. Try a checkout flow in your app using test card <code>4242 4242 4242 4242</code>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                  3. Check the <strong>Webhook Logs</strong> tab - a <code>checkout.session.completed</code> event should appear
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                  4. Verify the user's subscription status updated in the database
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Collapse>
      </Paper>

      {/* External Links */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" fontWeight={600}>
            External Links
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewRoundedIcon />}
              href="https://dashboard.stripe.com/test"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stripe Dashboard (Test)
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewRoundedIcon />}
              href="https://dashboard.stripe.com/test/webhooks"
              target="_blank"
              rel="noopener noreferrer"
            >
              Webhook Settings
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewRoundedIcon />}
              href="https://dashboard.stripe.com/test/apikeys"
              target="_blank"
              rel="noopener noreferrer"
            >
              API Keys
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewRoundedIcon />}
              href="https://dashboard.stripe.com/test/payments"
              target="_blank"
              rel="noopener noreferrer"
            >
              Test Payments
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function StripeManagement() {
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <PaymentRoundedIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight={800}>
          Stripe Management
        </Typography>
      </Stack>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<DashboardRoundedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Overview"
            sx={{ minHeight: 48, textTransform: "none" }}
          />
          <Tab
            icon={<ReceiptLongRoundedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Plans"
            sx={{ minHeight: 48, textTransform: "none" }}
          />
          <Tab
            icon={<WebhookRoundedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Webhook Logs"
            sx={{ minHeight: 48, textTransform: "none" }}
          />
          <Tab
            icon={<LocalOfferRoundedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Coupons"
            sx={{ minHeight: 48, textTransform: "none" }}
          />
          <Tab
            icon={<SettingsRoundedIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Settings"
            sx={{ minHeight: 48, textTransform: "none" }}
          />
        </Tabs>
      </Paper>

      <TabPanel value={tab} index={0}>
        <OverviewTab onNavigate={setTab} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <PlansTab showSnackbar={showSnackbar} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <WebhookLogsTab showSnackbar={showSnackbar} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <CouponsTab showSnackbar={showSnackbar} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <SettingsTab />
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
